import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal, MaskText } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import { ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1623874514711-0f321325f318?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const IMG2 = "https://images.unsplash.com/photo-1629231484522-f86ab156c34a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function SobreVorna() {
  const { t, tr } = useLang();
  const [hero, setHero] = useState({});
  useEffect(() => { api.get("/site").then((r) => setHero(r.data.hero || {})); }, [tr]);

  const CHAPTERS = [
    { n: "01", title: tr("Raíces Ecuatorianas", "Ecuadorian Roots"),
      text: tr("VORNA nació en Quito, a la sombra de los Andes, de la comunidad ecuatoriana de deportes de combate: los gimnasios, los tatamis y los peleadores que entrenan con puro corazón.",
               "VORNA was born in Quito, in the shadow of the Andes, out of Ecuador's combat sports community — the gyms, the mats, the fighters who train with pure heart.") },
    { n: "02", title: tr("Cultura de Combate", "Combat Culture"),
      text: tr("Es más que ropa. Es un uniforme para una forma de vida. Cada diseño lleva la cultura de la jaula: la disciplina del día a día y el respeto entre rivales.",
               "It's more than apparel. It's a uniform for a way of life. Every design carries the culture of the cage — the discipline of the daily grind and respect between opponents.") },
    { n: "03", title: tr("Rendimiento y Disciplina", "Performance & Discipline"),
      text: tr("Creamos productos que rinden cuando importa. Nada decorativo que no se gane su lugar. Hecho para sobrevivir el campamento completo.",
               "We build gear that performs when it matters. Nothing decorative that doesn't earn its place. Built to survive the whole camp.") },
    { n: "04", title: tr("Comunidad", "Community"),
      text: tr("VORNA respalda a atletas reales y es parte activa de la comunidad ecuatoriana de MMA, Jiu-Jitsu y grappling. Crecemos junto a nuestros peleadores.",
               "VORNA supports real athletes and is an active part of Ecuador's MMA, Jiu-Jitsu and grappling community. We grow alongside our fighters.") },
  ];

  return (
    <div data-testid="about-page">
      <Seo
        title={tr("Sobre VORNA | Marca Ecuatoriana de Deportes de Combate", "About VORNA | Ecuadorian Combat-Sports Brand")}
        description={tr("Conoce VORNA, la marca ecuatoriana de fightwear nacida para la pelea: nuestra historia, valores y atletas.", "Meet VORNA, the Ecuadorian fightwear brand built for the fight: our story, values and athletes.")}
        path="/sobre-vorna"
      />
      <section className="relative h-[85vh] overflow-hidden flex items-end">
        <img src={hero.about || HERO} alt="VORNA Ecuador" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/40" />
        <div className="relative mx-auto max-w-[1600px] w-full px-5 lg:px-10 pb-16">
          <p className="text-xs tracking-[0.35em] text-vorna-accent uppercase mb-4">{t("nav_about")}</p>
          <h1 className="font-display uppercase tracking-tighter text-[12vw] lg:text-[7.5vw] leading-[0.8]"><MaskText lines={tr(["Nacido en Ecuador.", "Hecho para la pelea."], ["Born in Ecuador.", "Built for the fight."])} /></h1>
        </div>
      </section>

      <div className="border-y border-white/10 py-5 bg-vorna-bg">
        <Marquee items={tr(["Disciplina", "Individualidad", "Rendimiento", "Comunidad", "Auténtico"], ["Discipline", "Individuality", "Performance", "Community", "Authentic"])} speed={32} />
      </div>

      <section className="mx-auto max-w-[1600px] px-5 lg:px-10 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden"><img src={hero.story || IMG2} alt="VORNA" className="h-full w-full object-cover" /></div>
              <p className="mt-4 text-xs text-white/30 tracking-widest uppercase">{tr("Nota: esta historia es un marcador — será reemplazada por la historia real de VORNA.", "Note: this story is a placeholder — it will be replaced with VORNA's real story.")}</p>
            </Reveal>
          </div>
          <div className="space-y-16">
            {CHAPTERS.map((c) => (
              <Reveal key={c.n}>
                <div className="flex gap-6">
                  <span className="font-display text-vorna-accent text-2xl shrink-0">{c.n}</span>
                  <div>
                    <h2 className="font-display uppercase tracking-tighter text-3xl lg:text-4xl leading-[0.9] mb-4">{c.title}</h2>
                    <p className="text-white/60 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-vorna-surface border-t border-white/10 py-20 lg:py-28 text-center">
        <Reveal>
          <h2 className="font-display uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.85] mb-8">{tr("Únete a la pelea", "Join the fight")}</h2>
          <Link to="/catalogo" className="inline-flex items-center gap-2 bg-vorna-accent px-8 py-4 font-display uppercase tracking-widest text-sm hover:bg-vorna-accentHover active:scale-95 transition-all">{t("cta_catalog")} <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </section>
    </div>
  );
}
